// Player coin launches (spec 8.4) and airdrops (8.6): dev buy, launch hype, the rug path
// (cash now, trust/heat/sleuths later) and the honest path (trust, slower hype decay).

import { TUNABLES } from "@degen/shared/config/tunables";
import { isBlocked } from "@degen/shared/config/blocklist";
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { addHeat, addTrust, playerClout } from "../player";
import { liveTokens, marketBuy } from "./market";
import { balanceOf, createToken, priceOf, transferTokens, type Token } from "./token";
import { scheduleInvestigation } from "../events/sleuth";
import { money } from "@degen/shared/money";

const L = TUNABLES.launch;

export interface LaunchRecord {
  tokenId: string;
  launchTick: number;
  devTokens: number; // tokens from the dev buy
  soldTokens: number; // dev tokens sold so far
  rugged: boolean;
  lastAirdropDay: number;
}

export interface LaunchCoinInput {
  name: string;
  ticker: string;
  description: string;
  devBuyUsd: number;
  iconSeed: number;
}

export type LaunchResult = { ok: true; tokenId: string } | { ok: false; error: string };

const reachOf = (state: GameState) => {
  const c = playerClout(state.player);
  return c / (c + 5_000);
};

export function launchCoin(state: GameState, rng: Rng, input: LaunchCoinInput): LaunchResult {
  const p = state.player;
  const day = dayOf(state.tick);
  if (p.flags.launchpadBanUntilDay !== undefined && day < p.flags.launchpadBanUntilDay) {
    return { ok: false, error: `You're banned from the launchpad until day ${p.flags.launchpadBanUntilDay}. Sleuths remember.` };
  }
  const name = input.name.trim();
  const ticker = input.ticker.trim().replace(/^\$/, "").toUpperCase();
  if (Array.from(name).length < 2 || Array.from(name).length > 24) return { ok: false, error: "Name: 2-24 characters." };
  if (!/^[A-Z0-9]{2,6}$/.test(ticker)) return { ok: false, error: "Ticker: 2-6 letters or numbers." };
  if (isBlocked(name, ticker)) return { ok: false, error: "That name belongs to a real project. Pick something original." };
  if (liveTokens(state.market).some((t) => t.ticker === ticker)) return { ok: false, error: `$${ticker} is taken.` };
  const devBuy = Math.max(0, input.devBuyUsd || 0);
  if (p.cash < L.coinFeeUsd + devBuy) return { ok: false, error: `You need ${money(L.coinFeeUsd)} + your dev buy.` };

  p.cash -= L.coinFeeUsd;
  const m = state.market;
  const t = createToken({
    id: `t${m.nextTokenId++}`,
    name,
    ticker,
    creator: "player",
    description: input.description.trim().slice(0, 80) || `${name}. Built different.`,
    iconSeed: input.iconSeed >>> 0,
    fate: "normal",
    hype: 0,
    bornTick: state.tick,
  });
  t.devId = "player"; // the player's wallet IS the dev wallet
  m.tokens[t.id] = t;
  m.stats.spawned++;

  let devTokens = 0;
  if (devBuy > 0) {
    const r = marketBuy(state, t, "player", devBuy);
    if (r.ok) {
      p.cash -= r.usd;
      devTokens = r.tokens;
      p.holdings.push({ kind: "token", tokenId: t.id, costUsd: r.usd });
    }
  }
  t.devBagPct = Math.round((devTokens / t.totalSupply) * 1000) / 10;
  t.hype = Math.min(100, L.hypeBase + L.hypeReach * reachOf(state) + rng.range(0, L.hypeRandom));
  p.launches.push({ tokenId: t.id, launchTick: state.tick, devTokens, soldTokens: 0, rugged: false, lastAirdropDay: 0 });
  p.stats.coinsLaunched++;
  pushLog(state, "player_launch", `You launched $${t.ticker}. Shill it and copytraders will come.`, { tokenId: t.id });
  return { ok: true, tokenId: t.id };
}

export function launchRecord(state: GameState, tokenId: string): LaunchRecord | undefined {
  return state.player.launches.find((l) => l.tokenId === tokenId);
}

/** Engine hook after the player sells: dumping your own dev bag early is a rug. */
export function onPlayerSellOwnToken(state: GameState, rng: Rng, t: Token, soldTokens: number) {
  const rec = launchRecord(state, t.id);
  if (!rec || rec.rugged || rec.devTokens <= 0) return;
  rec.soldTokens += soldTokens;
  const early = state.tick - rec.launchTick <= L.rugWindowTicks;
  if (!early || rec.soldTokens < rec.devTokens * L.rugSellShare) return;
  rec.rugged = true;
  t.rugged = true;
  t.hype = 0;
  const p = state.player;
  addTrust(p, -rng.range(L.rugTrust[0], L.rugTrust[1]));
  addHeat(p, L.rugHeat);
  p.stats.rugsPulled++;
  state.market.stats.rugged++;
  pushLog(state, "rug", `$${t.ticker} dev just dumped the whole bag. Rugged.`, { tokenId: t.id, byPlayer: 1 });
  pushLog(state, "player_rug", `You rugged $${t.ticker}. Cash in hand, trust in the gutter, heat rising. Sleuths are sniffing.`, { tokenId: t.id });
  scheduleInvestigation(state, rng, t.id);
}

/** Day boundary: honest builders earn trust; updates keep hype alive. */
export function launchesNewDay(state: GameState) {
  const p = state.player;
  for (const rec of p.launches) {
    const t = state.market.tokens[rec.tokenId];
    if (!t || rec.rugged || t.phase === "dead") continue;
    const ageDays = (state.tick - rec.launchTick) / TUNABLES.clock.ticksPerDay;
    if (ageDays > L.honestDays || rec.devTokens <= 0) continue;
    if (balanceOf(t, "player") >= rec.devTokens * L.honestHoldShare) {
      addTrust(p, L.honestTrustPerDay);
    }
  }
}

/** True if the player posted about this coin in the last 24h (slows its hype decay). */
export function recentlyUpdated(state: GameState, tokenId: string): boolean {
  const at = state.social.playerShills[tokenId];
  return at !== undefined && state.tick - at < TUNABLES.clock.ticksPerDay;
}

export interface AirdropInput {
  tokenId: string;
  pct: number; // 1-20 of your current bag
  target: "holders" | "followers";
}

export function launchAirdrop(state: GameState, rng: Rng, input: AirdropInput): LaunchResult {
  const p = state.player;
  const t = state.market.tokens[input.tokenId];
  const rec = launchRecord(state, input.tokenId);
  if (!t || !rec) return { ok: false, error: "You can only airdrop coins you launched." };
  if (t.phase === "dead" || rec.rugged) return { ok: false, error: "Nobody wants an airdrop of a dead coin." };
  const pct = Math.max(L.airdropPct[0], Math.min(L.airdropPct[1], input.pct));
  const bag = balanceOf(t, "player");
  const amount = (bag * pct) / 100;
  if (amount <= 0) return { ok: false, error: "Your bag is empty." };

  // Pick recipients.
  let recipients: string[];
  if (input.target === "holders") {
    recipients = Object.keys(t.holders).filter((id) => id.startsWith("n")).slice(0, 30);
    if (!recipients.length) return { ok: false, error: "No holders yet. Try your followers." };
  } else {
    const pool = state.npcs.filter((n) => n.archetype === "copy" || n.archetype === "degen" || n.archetype === "farmer");
    const n = Math.min(40, 5 + Math.floor(p.followers / 500));
    recipients = Array.from({ length: n }, () => rng.pick(pool).id);
  }
  const each = amount / recipients.length;
  const price = priceOf(t);
  for (const id of recipients) {
    transferTokens(t, "player", id, each);
    const npc = state.npcs[Number(id.slice(1))];
    if (npc && !npc.pos[t.id]) {
      // Some recipients dump fast, the rest hold like it's a gift.
      const dumper = rng.chance(L.airdropDumpShare);
      npc.pos[t.id] = { entry: price, peak: price, tick: state.tick, hold: dumper ? rng.int(10, 120) : rng.int(600, 3_000), target: 0, tookProfit: false };
    }
  }
  const h = p.holdings.find((x) => x.tokenId === t.id);
  if (h) h.costUsd *= 1 - pct / 100;

  // Reputation effects (once per coin per day).
  const day = dayOf(state.tick);
  if (rec.lastAirdropDay !== day) {
    rec.lastAirdropDay = day;
    const trust = L.airdropTrust[0] + ((L.airdropTrust[1] - L.airdropTrust[0]) * (pct - L.airdropPct[0])) / (L.airdropPct[1] - L.airdropPct[0]);
    addTrust(p, trust);
    t.hype = Math.min(100, t.hype + L.airdropHype);
    const reach = Math.pow(playerClout(p) + TUNABLES.social.baseReach, TUNABLES.social.reachExp);
    p.followers += Math.round(reach * pct * 0.05 * (p.trust / 100));
  }
  pushLog(state, "player_airdrop", `You airdropped ${pct}% of your $${t.ticker} bag to ${recipients.length} ${input.target}. 🪂`, { tokenId: t.id });
  return { ok: true, tokenId: t.id };
}
